import React, { useEffect, useState, useCallback } from 'react';
import { getCrossCommunityReport } from '../services/aggregateService';

// ─── Shared styles ─────────────────────────────────────────────────────────────
const S = {
  page:    { padding: '28px 32px', maxWidth: 1100, margin: '0 auto' },
  heading: { fontSize: 24, fontWeight: 700, marginBottom: 6 },
  sub:     { color: '#6b7280', fontSize: 14, marginBottom: 28 },
  section: { marginBottom: 32 },
  sTitle:  { fontSize: 16, fontWeight: 700, marginBottom: 12, color: '#1f2937' },
  table:   {
    width: '100%', borderCollapse: 'collapse',
    background: '#fff', borderRadius: 8, overflow: 'hidden',
    border: '1px solid #e5e7eb',
  },
  th:      {
    background: '#f3f4f6', padding: '10px 14px',
    textAlign: 'left', fontSize: 12, color: '#6b7280',
    fontWeight: 700, borderBottom: '1px solid #e5e7eb',
  },
  td:      {
    padding: '10px 14px', fontSize: 13, color: '#1f2937',
    borderBottom: '1px solid #f3f4f6',
  },
  bar:     (pct, color) => ({
    height: 12, borderRadius: 6,
    background: color, width: `${Math.max(pct * 100, 2)}%`,
    display: 'inline-block', verticalAlign: 'middle', marginRight: 8,
  }),
  err:     {
    background: '#fef2f2', border: '1px solid #fca5a5',
    borderRadius: 6, padding: '10px 14px', color: '#991b1b', fontSize: 14, marginBottom: 14,
  },
  badge:   (color) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 10,
    fontSize: 11, fontWeight: 700, background: color + '22', color,
  }),
};

const CATEGORY_LABEL = {
  plants_medicine: '🌿 Plants & Medicine',
  hunting:         '🦌 Hunting',
  birds:           '🦅 Birds',
  fishing:         '🎣 Fishing',
  environmental:   '🌍 Environmental',
  other:           '📍 Other',
};
const CATEGORY_COLOR = {
  plants_medicine: '#22c55e',
  hunting:         '#92400e',
  birds:           '#0ea5e9',
  fishing:         '#2563eb',
  environmental:   '#16a34a',
  other:           '#6b7280',
};
const SEASON_COLOR = {
  spring: '#22c55e',
  summer: '#f59e0b',
  fall:   '#ea580c',
  winter: '#3b82f6',
};

// Simple inline bar chart for count data
const BarRow = ({ label, count, max, color }) => (
  <tr>
    <td style={S.td}>{label}</td>
    <td style={S.td}>
      <span style={S.bar(count / max, color || '#6b7280')} />
      {count}
    </td>
  </tr>
);

const CrossCommunityReportPage = () => {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const { data: res } = await getCrossCommunityReport();
      setData(res);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load cross-community report.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div style={S.page}><p style={{ color: '#6b7280' }}>Loading report…</p></div>;
  if (error)   return <div style={S.page}><div style={S.err}>{error}</div></div>;
  if (!data)   return null;

  const { byTenant = [], byCategory = [], bySeason = [] } = data;
  const maxTenant   = Math.max(...byTenant.map(r => r.count),   1);
  const maxCategory = Math.max(...byCategory.map(r => r.count), 1);
  const maxSeason   = Math.max(...bySeason.map(r => r.count),   1);
  const totalRecords = byTenant.reduce((s, r) => s + r.count, 0);

  return (
    <div style={S.page}>
      <h1 style={S.heading}>Cross-Community Report</h1>
      <p style={S.sub}>
        Aggregate statistics across all participating communities — record counts by community,
        category, and season.
      </p>

      {/* ── Summary tiles ── */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginBottom: 28 }}>
        {[
          { num: totalRecords,       lbl: 'Total Approved Records', color: '#1d4ed8' },
          { num: byTenant.length,    lbl: 'Communities',            color: '#059669' },
          { num: byCategory.length,  lbl: 'Categories',             color: '#92400e' },
          { num: bySeason.length,    lbl: 'Seasons Reported',       color: '#7c3aed' },
        ].map(({ num, lbl, color }) => (
          <div key={lbl} style={{
            background: '#f9fafb', border: '1px solid #e5e7eb',
            borderRadius: 8, padding: '12px 18px', textAlign: 'center', minWidth: 120,
          }}>
            <div style={{ fontSize: 22, fontWeight: 700, color }}>{num}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>{lbl}</div>
          </div>
        ))}
      </div>

      {/* ── By Community ── */}
      <div style={S.section}>
        <div style={S.sTitle}>Records by Community</div>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Community</th>
              <th style={S.th}>Approved Records</th>
            </tr>
          </thead>
          <tbody>
            {byTenant.length === 0 && (
              <tr><td colSpan={2} style={{ ...S.td, color: '#6b7280' }}>No data yet.</td></tr>
            )}
            {byTenant.map((row) => (
              <BarRow
                key={row.tenantId}
                label={row.tenantName || row.tenantId}
                count={row.count}
                max={maxTenant}
                color="#1d4ed8"
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* ── By Category ── */}
      <div style={S.section}>
        <div style={S.sTitle}>Records by Category</div>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Category</th>
              <th style={S.th}>Count</th>
            </tr>
          </thead>
          <tbody>
            {byCategory.length === 0 && (
              <tr><td colSpan={2} style={{ ...S.td, color: '#6b7280' }}>No data yet.</td></tr>
            )}
            {byCategory.map((row) => (
              <BarRow
                key={row._id}
                label={CATEGORY_LABEL[row._id] || row._id}
                count={row.count}
                max={maxCategory}
                color={CATEGORY_COLOR[row._id] || '#6b7280'}
              />
            ))}
          </tbody>
        </table>
      </div>

      {/* ── By Season ── */}
      <div style={S.section}>
        <div style={S.sTitle}>Records by Season</div>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Season</th>
              <th style={S.th}>Count</th>
            </tr>
          </thead>
          <tbody>
            {bySeason.length === 0 && (
              <tr><td colSpan={2} style={{ ...S.td, color: '#6b7280' }}>No data yet.</td></tr>
            )}
            {bySeason.map((row) => (
              <BarRow
                key={row._id}
                label={row._id ? row._id.charAt(0).toUpperCase() + row._id.slice(1) : '—'}
                count={row.count}
                max={maxSeason}
                color={SEASON_COLOR[row._id] || '#6b7280'}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CrossCommunityReportPage;
